/**
 * JobCard - Unified Job Display Component
 * ========================================
 * Single premium job card used across contractor/internal/client views.
 * Shows richer essential information with consistent styling.
 * Permission filtering is handled server-side.
 */
import React from "react";

function formatDate(value) {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-GB");
  } catch {
    return value;
  }
}

function priorityStyles(priority) {
  const p = String(priority || "").toLowerCase();
  if (p.includes("critical") || p.includes("urgent") || p.includes("emergency") || p.includes("high")) {
    return "bg-red-100 text-red-700";
  }
  if (p.includes("medium")) {
    return "bg-amber-100 text-amber-700";
  }
  return "bg-emerald-100 text-emerald-700";
}

function stageStyles(stage) {
  const s = String(stage || "").toLowerCase();
  if (s.includes("completed") || s.includes("closed")) return "bg-emerald-100 text-emerald-700";
  if (s.includes("approval") || s.includes("awaiting")) return "bg-amber-100 text-amber-700";
  if (s.includes("reassign") || s.includes("failed") || s.includes("declined")) return "bg-red-100 text-red-700";
  if (s.includes("scheduled") || s.includes("assigned")) return "bg-blue-100 text-blue-700";
  return "bg-slate-100 text-slate-700";
}

export default function JobCard({ job, variant = "internal", actions = {}, footer = null }) {
  const isOverdue =
    job?.due_date && !Number.isNaN(new Date(job.due_date).getTime())
      ? new Date(job.due_date) < new Date()
      : false;

  if (!job) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="job-card-error">
        Job data unavailable.
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`job-card-${job.job_id || 'unknown'}`}>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="text-xl font-semibold text-slate-900">
            {job.park_name || "-"}{job.unit_name ? ` - Plot ${job.unit_name}` : ""}
          </div>
          <div className="mt-1 text-sm text-slate-500">
            {job.job_reference || "-"}
          </div>
          <div className="mt-2 text-base font-medium text-slate-800">
            {job.description || "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${priorityStyles(job.priority)}`} data-testid="priority-badge">
            {job.priority || "Standard"}
          </span>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${stageStyles(job.stage)}`} data-testid="stage-badge">
            {job.stage || "-"}
          </span>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Location */}
        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
          <div className="mb-2 text-sm font-semibold text-slate-600">Location</div>
          <div className="text-sm text-slate-900">{job.full_address || job.park_name || "-"}</div>
          <div className="mt-1 text-sm text-slate-500">{job.postcode || "-"}</div>
          {job.postcode && (
            <a 
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((job.full_address || job.park_name || '') + ' ' + job.postcode)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-sm text-teal-700 underline hover:text-teal-800"
              data-testid="maps-link"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Open in Maps
            </a>
          )}
        </div>

        {/* Contact */}
        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
          <div className="mb-2 text-sm font-semibold text-slate-600">Contact</div>
          <div className="text-sm text-slate-900">{job.contact_name || "-"}</div>
          {job.contact_phone ? (
            <a href={`tel:${job.contact_phone}`} className="mt-1 block text-sm text-teal-700 underline" data-testid="contact-phone">
              {job.contact_phone}
            </a>
          ) : (
            <div className="mt-1 text-sm text-slate-500">-</div>
          )}
          {job.contact_email ? (
            <a href={`mailto:${job.contact_email}`} className="mt-1 block text-sm text-teal-700 underline" data-testid="contact-email">
              {job.contact_email}
            </a>
          ) : (
            <div className="mt-1 text-sm text-slate-500">-</div>
          )}
        </div>

        {/* Job Details */}
        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
          <div className="mb-2 text-sm font-semibold text-slate-600">Job Details</div>
          <div className="text-sm text-slate-900">
            <span className="font-medium">Type:</span> {job.job_type || "-"}
          </div>
          {job.trade && (
            <div className="mt-1 text-sm text-slate-900">
              <span className="font-medium">Trade:</span> {job.trade}
            </div>
          )}
          <div className="mt-2 text-sm text-slate-900">
            <span className="font-medium">Access:</span> {typeof job.access_notes === 'string' ? job.access_notes : (Array.isArray(job.access_notes) ? 'See notes' : "No access notes")}
          </div>
        </div>

        {/* Timeline */}
        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
          <div className="mb-2 text-sm font-semibold text-slate-600">Timeline</div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-slate-500">Created</div>
              <div className="font-medium text-slate-900">{formatDate(job.created_date)}</div>
            </div>
            <div>
              <div className="text-slate-500">Scheduled</div>
              <div className="font-medium text-slate-900">{formatDate(job.scheduled_date)}</div>
            </div>
            <div>
              <div className="text-slate-500">Due</div>
              <div className={`font-medium ${isOverdue ? "text-red-600" : "text-slate-900"}`} data-testid="due-date">
                {formatDate(job.due_date)}
                {isOverdue && <span className="ml-1 text-xs">(OVERDUE)</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Internal commercial section */}
      {variant === "internal" && (job.quote_value !== undefined || job.po_reference || job.invoice_id) && (
        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4" data-testid="commercial-section">
          <div className="mb-2 text-sm font-semibold text-slate-600">Commercial</div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-slate-500">Quote</div>
              <div className="font-medium text-slate-900">
                {job.quote_value != null ? `£${Number(job.quote_value).toFixed(2)}` : "-"}
              </div>
            </div>
            <div>
              <div className="text-slate-500">PO</div>
              <div className="font-medium text-slate-900">{job.po_reference || "-"}</div>
            </div>
            <div>
              <div className="text-slate-500">Invoice</div>
              <div className="font-medium text-slate-900">{job.invoice_id || "-"}</div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-3">
        {variant === "contractor" && (
          <>
            {actions.onAccept && (
              <button
                onClick={actions.onAccept}
                className="min-w-[130px] rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 transition-colors"
                data-testid="accept-btn"
              >
                Accept Job
              </button>
            )}
            {actions.onDecline && (
              <button
                onClick={actions.onDecline}
                className="min-w-[130px] rounded-2xl bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-red-700 transition-colors"
                data-testid="decline-btn"
              >
                Decline
              </button>
            )}
            {actions.onSchedule && (
              <button
                onClick={actions.onSchedule}
                className="min-w-[130px] rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
                data-testid="schedule-btn"
              >
                Schedule
              </button>
            )}
            {actions.onQuote && (
              <button
                onClick={actions.onQuote}
                className="min-w-[130px] rounded-2xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 transition-colors"
                data-testid="quote-btn"
              >
                Submit Quote
              </button>
            )}
            {actions.onDownloadPdf && (
              <button
                onClick={actions.onDownloadPdf}
                className="min-w-[130px] rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 transition-colors"
                data-testid="download-pdf-btn"
              >
                Download PDF
              </button>
            )}
            {actions.onWhatsApp && (
              <button
                onClick={actions.onWhatsApp}
                className="min-w-[130px] rounded-2xl bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#128C7E] transition-colors"
                data-testid="whatsapp-btn"
              >
                WhatsApp
              </button>
            )}
          </>
        )}

        {variant === "internal" && (
          <>
            {actions.onAssign && (
              <button
                onClick={actions.onAssign}
                className="rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
                data-testid="assign-btn"
              >
                Assign
              </button>
            )}
            {actions.onApprove && (
              <button
                onClick={actions.onApprove}
                className="rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 transition-colors"
                data-testid="approve-btn"
              >
                Approve
              </button>
            )}
            {actions.onInvoice && (
              <button
                onClick={actions.onInvoice}
                className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 transition-colors"
                data-testid="invoice-btn"
              >
                Create Invoice
              </button>
            )}
            {actions.onDownloadPdf && (
              <button
                onClick={actions.onDownloadPdf}
                className="rounded-2xl bg-slate-200 px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm hover:bg-slate-300 transition-colors"
                data-testid="download-pdf-btn"
              >
                Download PDF
              </button>
            )}
          </>
        )}

        {variant === "client" && (
          <>
            {actions.onDownloadPdf && (
              <button
                onClick={actions.onDownloadPdf}
                className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 transition-colors"
                data-testid="download-pdf-btn"
              >
                Download PDF
              </button>
            )}
          </>
        )}
      </div>

      {footer ? <div className="mt-4">{footer}</div> : null}
    </div>
  );
}

// Named exports for specific variants
export function ContractorJobCard({ job, actions, footer }) {
  return <JobCard job={job} variant="contractor" actions={actions} footer={footer} />;
}

export function InternalJobCard({ job, actions, footer }) {
  return <JobCard job={job} variant="internal" actions={actions} footer={footer} />;
}

export function ClientJobCard({ job, actions, footer }) {
  return <JobCard job={job} variant="client" actions={actions} footer={footer} />;
}
